import {
  LimitedResultOfTaskGroupDto,
  TaskGroupDto,
  TaskGroupsClient,
} from '@r1-development/resolution-mechanicalturk-api-client';
import {
  GetTaskGroupsParams,
  GetTaskGroupsParamsOriginal,
  GetMyTaskGroupsParams,
  GetMyTaskGroupsParamsOriginal,
  GetMyAvailableTaskGroupsParams,
  GetMyAvailableTaskGroupsParamsOriginal,
} from '../models/tasks.model';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { Prettify } from '../models/typescript-types';
import { loadCursorBasedApiGenerator } from './global.utils';
import { firstValueFrom } from 'rxjs';

export function convertTaskDtoTo<T extends { patientName: string }>(
  task: TaskGroupDto
): T {
  return {
    ...task,
  } as T;
}

export function convertToGetTaskGroupsParams(
  params: GetTaskGroupsParams
): GetTaskGroupsParamsOriginal {
  return [
    params.filterBySpecializations,
    params.limit,
    params.assigned,
    params.continuationToken,
    params.status,
    params.assignedTo,
    params.completedBy,
    params.sort,
    params.completedSince,
    null, // completedBefore not available
    params.workflowInstanceId ?? null,
    params.taskTypes,
    params.searchTerm,
    params.taskStatus,
    params.taskIds,
    null, // specializations not available
    null, // serviceLines not available
    null, // globalEligible not available
  ];
}

export function convertToGetMyTaskGroupsParams(
  params: GetMyTaskGroupsParams
): GetMyTaskGroupsParamsOriginal {
  return [
    params.includeUnassigned,
    params.sort,
    params.status,
    params.searchTerm,
    params.completedSince,
    params.workflowInstanceId,
    params.taskTypes,
    params.limit,
    params.continuationToken,
  ];
}

export function convertToGetMyAvailableTaskGroupsParams(
  params: GetMyAvailableTaskGroupsParams
): GetMyAvailableTaskGroupsParamsOriginal {
  return [
    params.sort,
    params.searchTerm,
    params.taskTypes,
    params.limit,
    params.continuationToken,
  ];
}

/**
 * Walks every page of the task-groups worklist, invoking `cb` per page.
 *
 * @param abortSignal Optional signal to stop the walk early - pass a component's
 * DestroyRef-backed signal so navigating away does not keep paging. The worklist can run
 * to dozens of sequential pages, so an abandoned walk is both wasted load on the API and a
 * stream of updates into a component nobody is looking at.
 *
 * The pages are driven manually rather than with `for await` so the signal can be checked
 * *before* requesting each page - a `for await` loop only regains control after its next
 * request has already been issued, which would fire one wasted request per abort. A request
 * already in flight when the abort happens still completes; it cannot be cancelled from
 * here. `finalize` is skipped on abort so a torn-down caller is never called back.
 */
export async function loadTasks(
  this: ThisParameterType<unknown>,
  injector: Injector,
  cb: (groups: TaskGroupDto[], callCount: number) => void,
  initialParams?: Prettify<Omit<GetTaskGroupsParams, 'continuationToken'>>,
  finalize?: () => void,
  abortSignal?: AbortSignal
) {
  const taskGroupsClient = runInInjectionContext(injector, () =>
    inject(TaskGroupsClient)
  );
  let callTimes = 0;

  const pages = loadCursorBasedApiGenerator<
    LimitedResultOfTaskGroupDto,
    GetTaskGroupsParams
  >(
    (params: GetTaskGroupsParams) => {
      return firstValueFrom(
        taskGroupsClient.getTaskGroups(...convertToGetTaskGroupsParams(params))
      );
    },
    { ...(initialParams ?? {}) }
  );

  try {
    while (!abortSignal?.aborted) {
      const page = await pages.next();
      if (page.done) {
        break;
      }
      // Abort may have landed while that request was in flight.
      if (abortSignal?.aborted) {
        break;
      }
      cb.call(this, page.value.items!, ++callTimes);
    }
  } finally {
    // Release the generator, whether we finished the walk or bailed out of it.
    await pages.return(undefined);
  }

  if (abortSignal?.aborted) {
    return;
  }

  if (finalize) {
    finalize.call(this);
  }
}
